import React, { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@stores/auth";
import { useNotificationStore } from "@stores/notifications/notificationStore";

const UNREAD_POLL_INTERVAL_MS = 15_000;

export const NotificationStreamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, billingOnly } = useAuth();
    const queryClient = useQueryClient();
    const startSse = useNotificationStore((s) => s.startSse);
    const stopSse = useNotificationStore((s) => s.stopSse);
    const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount);
    const subscribeRealtime = useNotificationStore((s) => s.subscribeRealtime);

    useEffect(
        () => subscribeRealtime(() => void queryClient.invalidateQueries({ queryKey: ["notifications"] })),
        [queryClient, subscribeRealtime]
    );

    useEffect(() => {
        if (!isAuthenticated || billingOnly) {
            stopSse();
            return;
        }

        void fetchUnreadCount();
        startSse();

        let pollId: number | undefined;
        const sync = () => void useNotificationStore.getState().fetchUnreadCount();
        const startPoll = () => {
            if (pollId === undefined) pollId = window.setInterval(sync, UNREAD_POLL_INTERVAL_MS);
        };
        const stopPoll = () => {
            if (pollId !== undefined) {
                clearInterval(pollId);
                pollId = undefined;
            }
        };
        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                sync();
                startPoll();
            } else {
                stopPoll();
            }
        };

        // Leaving the page must free its live connection: browsers hold only ~6 per host, and a
        // stream kept by a page in the back/forward cache would queue every later request.
        const onPageHide = () => stopSse();
        const onPageShow = (e: PageTransitionEvent) => {
            if (e.persisted) startSse();
        };

        if (document.visibilityState === "visible") startPoll();
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("online", sync);
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("pageshow", onPageShow);

        return () => {
            stopPoll();
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("online", sync);
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("pageshow", onPageShow);
            stopSse();
        };
    }, [isAuthenticated, billingOnly, startSse, stopSse, fetchUnreadCount]);

    return <>{children}</>;
};
